import { ChevronDown, LogOut, Menu, Moon, Sun } from "lucide-react";

import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { useIsMobile } from "../lib/useMediaQuery";
import { useMqttStatus } from "../hooks/useMqtt";

interface TopbarProps {
  navCollapsed: boolean;
  onMenuClick: () => void;
}

export function Topbar({ navCollapsed, onMenuClick }: TopbarProps) {
  const { theme, toggle } = useTheme();
  const { state, logout } = useAuth();
  const email = state.status === "authenticated" ? state.email : "";
  const mqttStatus = useMqttStatus();
  const isMobile = useIsMobile();

  // On mobile, bump the 32px icon buttons to the 44px tap floor.
  const iconButtonResponsive: React.CSSProperties = isMobile
    ? { ...iconButton, height: "var(--tap-min)", width: "var(--tap-min)" }
    : iconButton;

  const mqttColor =
    mqttStatus === "connected"
      ? "var(--success)"
      : mqttStatus === "connecting"
        ? "var(--warning)"
        : "var(--text-muted)";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "var(--bg-secondary)",
        borderBottom: "1px solid var(--border-subtle)",
        fontSize: 13,
        color: "var(--text-secondary)",
        fontFamily: "var(--font-sans)",
        height: 56,
        padding: "0 28px",
        gap: 22,
      }}
    >
      {/* Hamburger — first child, mobile/collapsed-nav only */}
      {navCollapsed ? (
        <button
          onClick={onMenuClick}
          aria-label="Open navigation"
          title="Open navigation"
          style={hamburgerButton}
        >
          <Menu size={20} strokeWidth={1.6} />
        </button>
      ) : (
        <span aria-hidden />
      )}

      {/* Right-hand cluster */}
      <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
        {/* Region marker — hidden on phones */}
        {!isMobile && (
          <>
            <span style={{ color: "var(--text-muted)", fontSize: 12.5 }}>
              Production · us-west-2
            </span>
            <Separator />
          </>
        )}

        {/* MQTT status — dot always; "Live feed" label hidden on phones */}
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12.5,
            color: "var(--text-muted)",
          }}
          title={`MQTT ${mqttStatus} — real-time call-state pushes from the worker.`}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: mqttColor,
            }}
          />
          {!isMobile && "Live feed"}
        </span>

        <Separator />

        {/* Theme toggle */}
        <button
          onClick={toggle}
          aria-label="toggle theme"
          title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          style={iconButtonResponsive}
        >
          {theme === "dark" ? <Moon size={14} strokeWidth={1.5} /> : <Sun size={14} strokeWidth={1.5} />}
        </button>

        {/* Operator menu — email + chevron collapse to just the avatar on phones */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 10px",
            borderRadius: "var(--radius-input)",
            color: "var(--text-primary)",
            fontSize: 13,
            cursor: "default",
          }}
        >
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: "var(--accent)",
              color: "white",
              display: "grid",
              placeItems: "center",
              fontSize: 11,
              fontWeight: 600,
            }}
            aria-hidden
            title={isMobile ? email : undefined}
          >
            {(email[0] ?? "?").toUpperCase()}
          </span>
          {!isMobile && <span>{email}</span>}
          {!isMobile && <ChevronDown size={12} strokeWidth={1.5} color="var(--text-muted)" />}
        </div>

        <button
          onClick={() => void logout()}
          title="Sign out"
          aria-label="sign out"
          style={iconButtonResponsive}
        >
          <LogOut size={13} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

function Separator() {
  return (
    <span
      aria-hidden
      style={{
        width: 1,
        height: 18,
        background: "var(--border-subtle)",
      }}
    />
  );
}

const iconButton: React.CSSProperties = {
  background: "transparent",
  border: "none",
  height: 32,
  width: 32,
  display: "grid",
  placeItems: "center",
  color: "var(--text-secondary)",
  cursor: "pointer",
  borderRadius: "var(--radius-input)",
};

const hamburgerButton: React.CSSProperties = {
  background: "transparent",
  border: "none",
  height: "var(--tap-min)",
  width: "var(--tap-min)",
  display: "grid",
  placeItems: "center",
  color: "var(--text-primary)",
  cursor: "pointer",
  borderRadius: "var(--radius-input)",
  marginLeft: -8,
};
